import { Button } from "@components/ui/button";
import { useAuth } from "@hooks/useAuth";
import useDialog from "@hooks/useDialog";
import { PlanProperties } from "@hooks/usePlans/types";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import { AddOn, UserSubscription } from "@hooks/useUserSubscriptions/types";
import { Label } from "@radix-ui/react-dropdown-menu";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { serverEndpoints } from "@utils/serverEndpoints";
import { authClient } from "@utils/authClient";
import { supabase } from "@utils/serverClient";
import React, { useState } from "react";
import { GrAddCircle, GrSubtractCircle } from "react-icons/gr";

export default function AddMusicZoneAddOnDialog({
  onClose,
}: {
  onClose: () => void;
}) {
  const [numberOfMusicZones, setNumberOfMusicZones] = useState(1);

  const { showSuccessSnackbar, showErrorSnackbar, openDialog } = useDialog();

  const { subscription, planProperties, addOn } = useUserSubscriptions();

  const { user, organization } = useAuth();

  const queryClient = useQueryClient();

  const { mutate: addMusicZones, isPending } = useMutation({
    mutationFn: async ({
      subscription,
      addOn,
      totalMusicZones,
    }: {
      subscription?: UserSubscription;
      addOn?: AddOn;
      totalMusicZones: number;
    }) => {
      const org_id = organization?.id;
      if (!user || !org_id || !subscription?.planId) return;

      const accessToken = await authClient.getAccessToken();
      const _headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "user-id": user?.id ?? "",
        "business-id": org_id ?? "",
      };

      const _newAddOn: Partial<AddOn> = {
        ...(addOn ?? {}),
        businessId: org_id,
        planId: subscription?.planId,
        subscriptionId: subscription.id,
        userId: user?.id,
        properties: JSON.stringify({
          ...JSON.parse(addOn?.properties ?? "{}"),
          entitled_music_zones: totalMusicZones,
        }),
      };
      const addOnData = await fetch(
        addOn ? serverEndpoints.updateAddOn : serverEndpoints.createAddOn,
        {
          method: "POST",
          body: JSON.stringify({
            addOn: _newAddOn,
          }),
          headers: _headers,
        },
      );
      const _json = await addOnData.json();

      return _json;
    },
    onSuccess() {
      showSuccessSnackbar("Music zones added successfully.");
      onClose();
    },
    onError() {
      showErrorSnackbar("Something went wrong.");
    },
    onSettled(data) {
      if (data?.success) {
        queryClient.setQueryData(["addOn"], () => {
          return data;
        });
      }
    },
  });

  function formatMoney(amount: number) {
    if (!subscription) return "";
    return `${amount.toLocaleString("en-US", {
      style: "currency",
      maximumFractionDigits: 0,
      currency: subscription.plan.currency,
    })}`;
  }

  if (!subscription || !planProperties.addon_music_cost) return;

  return (
    <div className="flex flex-col gap-2 pt-4">
      <Label>How many screens would you like to add music for?</Label>
      <div className="flex items-end justify-center gap-2 py-4">
        <Button
          disabled={numberOfMusicZones === 1}
          onClick={() => {
            setNumberOfMusicZones((n) => n - 1);
          }}
        >
          <GrSubtractCircle />
        </Button>
        <p className="w-12 rounded-sm bg-muted p-2 text-center text-sm font-medium">
          {numberOfMusicZones}
        </p>
        <Button
          onClick={() => {
            setNumberOfMusicZones((n) => n + 1);
          }}
        >
          <GrAddCircle />
        </Button>
        <p className="text-xs">
          at <Money>{formatMoney(planProperties.addon_music_cost)}</Money>{" "}
          <span className="text-xs font-medium text-gray-400">
            per month per screen
          </span>
        </p>
      </div>

      <p className="text-sm">
        The monthly cost will increase from{" "}
        <Money>{formatMoney(subscription.amount)}</Money> to{" "}
        <Money>
          {formatMoney(
            subscription.amount +
            numberOfMusicZones * planProperties.addon_music_cost,
          )}
        </Money>
      </p>

      <div className="flex items-center justify-end gap-2 pt-4">
        <Button
          variant={"ghost"}
          size={"sm"}
          onClick={onClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (!planProperties?.addon_music_cost) return;
            openDialog({
              title: `Are you sure you want to add music integration for ${numberOfMusicZones} screen${numberOfMusicZones > 1 ? 's' : ''}?`,
              description: `This will increase your monthly subscription cost from ${formatMoney(
                subscription.amount,
              )} to ${formatMoney(
                subscription.amount +
                numberOfMusicZones * planProperties.addon_music_cost,
              )}`,
              dialogType: "confirm",
              onConfirm: async () => {
                const props: PlanProperties = {
                  ...planProperties,
                  entitled_music_zones:
                    (planProperties.entitled_music_zones ?? 0) + numberOfMusicZones,
                };
                delete props.features;
                delete props.addon_music_cost;
                addMusicZones({
                  subscription: subscription,
                  addOn: addOn,
                  totalMusicZones:
                    (planProperties.entitled_music_zones ?? 0) + numberOfMusicZones,
                });
              },
            });
          }}
          disabled={isPending}
        >
          Proceed
        </Button>
      </div>
    </div>
  );
}

function Money({ children }: { children: React.ReactNode }) {
  return <span className="text-xl font-medium text-primary">{children}</span>;
}
